import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import { getOutstandingQrBills, verifyQrPayment } from "../../services";
import { OutstandingQrBill } from "../../types/sale-deposit.type";
import {
  QrPayment,
  getQrStatusLabel,
  isSettled,
} from "../../types/qr-payment.type";
import { formatNumber } from "../../utils";

interface OutstandingQrModalProps {
  open: boolean;
  depositUuid: string | null;
  routeCode: string;
  businessDate: string;
  onClose: () => void;
  /** Fired when a check found money, so the row's figure can be reloaded. */
  onSettled: () => void;
}

/**
 * The bills a route-day's เงินโอน is short by, and the means to close them.
 *
 * Deliberately not a read-only list. A QR bill goes outstanding for one ordinary reason —
 * the customer said they would transfer later — and the fix is a single question to the
 * bank. Putting that question here means the reviewer clears the day from the screen that
 * told them it was not clear, instead of memorising bill numbers and going hunting on the
 * sales page.
 *
 * A hit settles the bill server-side in the same call, so `onSettled` reloads the row and
 * the figure behind the warning drops as the list is worked through.
 *
 * A bill whose QR record predates the bill link has no `qr_payment_uuid` and cannot be
 * chased from here. It is still shown — it is still money missing — with the button
 * disabled and the reason in a tooltip, rather than quietly filtered out.
 */
export default function OutstandingQrModal({
  open,
  depositUuid,
  routeCode,
  businessDate,
  onClose,
  onSettled,
}: OutstandingQrModalProps) {
  const [bills, setBills] = useState<OutstandingQrBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [checkingUuid, setCheckingUuid] = useState<string | null>(null);

  const fetchBills = useCallback(async () => {
    if (!depositUuid) return;
    setLoading(true);
    setFailed(false);
    try {
      const {
        data: { data },
      } = await getOutstandingQrBills(depositUuid);
      setBills(Array.isArray(data) ? data : []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [depositUuid]);

  useEffect(() => {
    if (open) fetchBills();
  }, [open, fetchBills]);

  const handleCheck = (bill: OutstandingQrBill) => async () => {
    if (!bill.qr_payment_uuid) return;
    setCheckingUuid(bill.qr_payment_uuid);
    try {
      const {
        data: { data },
      } = await verifyQrPayment(bill.qr_payment_uuid);
      if (isSettled((data as QrPayment).status)) {
        // Settled bills leave the list by definition — reload rather than patch, so the
        // row and the modal cannot disagree about what is still outstanding.
        await fetchBills();
        onSettled();
      } else {
        setBills((prev) =>
          prev.map((row) =>
            row.bill_uuid === bill.bill_uuid
              ? { ...row, qr_status: (data as QrPayment).status }
              : row,
          ),
        );
      }
    } catch {
      // Surfaced by the axios interceptor.
    } finally {
      setCheckingUuid(null);
    }
  };

  const outstandingTotal = bills.reduce((sum, bill) => sum + bill.amount, 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        QR ที่ยังไม่พบเงินเข้า
        <Typography variant="body2" color="text.secondary">
          {routeCode} · {businessDate}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <>
            <Skeleton variant="rounded" height={44} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" height={44} />
          </>
        )}

        {!loading && failed && (
          <Alert severity="error">
            โหลดรายการไม่สำเร็จ กรุณาลองใหม่
          </Alert>
        )}

        {!loading && !failed && bills.length === 0 && (
          <Alert severity="success">
            ไม่มี QR ค้างแล้ว — ยอดเงินโอนของวันนี้ครบตามที่แสดง
          </Alert>
        )}

        {!loading && !failed && bills.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              รวม {bills.length} ใบ {formatNumber(outstandingTotal)} บาท
              ยังไม่ถูกนับในยอดเงินโอน หากเงินเข้าภายหลัง
              ยอดของวันนี้จะเพิ่มขึ้น
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>เลขที่บิล</TableCell>
                  <TableCell>ร้านค้า</TableCell>
                  <TableCell align="right">ยอด</TableCell>
                  <TableCell>สถานะ QR</TableCell>
                  <TableCell align="center">ตรวจสอบ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.map((bill) => (
                  <TableRow key={bill.bill_uuid}>
                    <TableCell>{bill.bill_no ?? "-"}</TableCell>
                    <TableCell>
                      {bill.customer_name ?? bill.customer_code ?? "-"}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(bill.amount)}
                    </TableCell>
                    <TableCell>
                      {bill.qr_status
                        ? getQrStatusLabel(
                            bill.qr_status as QrPayment["status"],
                          )
                        : "ไม่มีข้อมูล QR"}
                    </TableCell>
                    <TableCell align="center">
                      {bill.qr_payment_uuid ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleCheck(bill)}
                          disabled={checkingUuid !== null}
                          startIcon={
                            checkingUuid === bill.qr_payment_uuid ? (
                              <CircularProgress size={14} />
                            ) : undefined
                          }
                        >
                          ตรวจสอบกับธนาคาร
                        </Button>
                      ) : (
                        <Tooltip title="บิลนี้ไม่มีข้อมูล QR ในระบบ (บิลก่อนเริ่มผูก QR กับบิล) — ต้องตรวจสอบกับธนาคารเอง">
                          <span>
                            <Button size="small" variant="outlined" disabled>
                              ตรวจสอบกับธนาคาร
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} variant="contained">
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
